import { parseAnalysisItem } from './analysisResponseParser';
import imgUrbanLight from '../../../assets/PNG/urbanCompass_light.png';
const COLORS = {
  primary: '#1d4ed8',
  primaryLight: '#eff6ff',

  border: '#d1d5db',

  textPrimary: '#111827',
  textSecondary: '#374151',
  textMuted: '#6b7280',

  success: '#059669',
  successBg: '#ecfdf5',

  warning: '#b45309',
  warningBg: '#fffbeb',

  danger: '#dc2626',
  dangerBg: '#fef2f2',
};

const escapeHtml = (str = '') =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br/>');

const formatDateTime = (date) =>
  date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const overallStatus = (avg) => {
  if (avg >= 90) return { label: 'Passed', tone: 'success' };
  if (avg >= 70) return { label: 'Mostly Matched', tone: 'success' };
  if (avg >= 40) return { label: 'Review Required', tone: 'warning' };
  return { label: 'Failed', tone: 'danger' };
};

const headerStatus = (avg) => {
  if (avg >= 80) return { label: 'Completed', tone: 'success' };
  if (avg >= 40) return { label: 'Review Required', tone: 'warning' };
  return { label: 'Failed', tone: 'danger' };
};

const recommendationFor = (avg) => {
  if (avg >= 90)
    return {
      title: 'No action required.',
      tone: 'success',
      body: 'All checked fields are consistent across documents. This application can proceed without additional review.',
    };
  if (avg >= 70)
    return {
      title: 'Minor review recommended.',
      tone: 'success',
      body: 'Most fields matched successfully. Please briefly review the partial matches listed above before approval.',
    };
  if (avg >= 40)
    return {
      title: 'Manual review recommended.',
      tone: 'warning',
      body: 'Several fields show partial or failed matches. Please verify the highlighted differences before approval.',
    };
  return {
    title: 'Application requires manual verification.',
    tone: 'danger',
    body: 'Multiple fields failed to match across the submitted documents. Do not approve until all differences below are resolved.',
  };
};

const badge = (label, tone) => `<span class="badge badge--${tone}">${escapeHtml(label)}</span>`;

const buildSummaryCards = ({ total, matched, partial, failed, avg }) => {
  const s = overallStatus(avg);
  return `
  <div class="summary-grid" data-report-block>
    <div class="score-panel">
      <div class="score-ring score-ring--${s.tone}"><span class="score-ring__value">${avg}%</span></div>
      ${badge(s.label, s.tone)}
      <div class="score-panel__caption">Overall Verification Score</div>
    </div>
    <div class="summary-cards">
      <div class="summary-card"><span class="summary-card__value">${total}</span><span class="summary-card__label">Total Fields Checked</span></div>
      <div class="summary-card summary-card--success"><span class="summary-card__value">${matched}</span><span class="summary-card__label">Matched Fields</span></div>
      <div class="summary-card summary-card--warning"><span class="summary-card__value">${partial}</span><span class="summary-card__label">Partial Matches</span></div>
      <div class="summary-card summary-card--danger"><span class="summary-card__value">${failed}</span><span class="summary-card__label">Failed Matches</span></div>
    </div>
  </div>`;
};

const buildComparisonTable = (comparisons) => {
  if (!comparisons.length) return '';
  const rows = comparisons
    .map(
      (c) => `
      <tr class="${c.status === 'Different' ? 'row--diff' : ''}">
        <td>${escapeHtml(c.document)}</td>
        <td class="mono">${escapeHtml(c.value)}</td>
        <td>${badge(c.status, c.status === 'Match' ? 'success' : 'danger')}</td>
      </tr>`
    )
    .join('');
  return `<table class="compare-table"><thead><tr><th>Document</th><th>Value</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>`;
};

const buildExpectedFoundBox = (ef, reason) => {
  if (!ef) return '';
  return `
    <div class="diff-box">
      <div class="diff-box__col diff-box__col--found"><span class="diff-box__label">Found Value</span><span class="diff-box__value">${escapeHtml(ef.found)}</span></div>
      <div class="diff-box__col diff-box__col--expected"><span class="diff-box__label">Expected Value</span><span class="diff-box__value">${escapeHtml(ef.expected)}</span></div>
      <div class="diff-box__col diff-box__col--reason"><span class="diff-box__label">Reason</span><span class="diff-box__value">${escapeHtml(reason)}</span></div>
    </div>`;
};

const buildAnalysisCard = (item, index) => {
  const p = parseAnalysisItem(item);
  return `
    <div class="analysis-card" data-report-block>
      <div class="analysis-card__header">
        <div class="analysis-card__title-row">
          <span class="analysis-card__index">${String(index + 1).padStart(2, '0')}</span>
          <span class="analysis-card__title">${escapeHtml(item.title)}</span>
        </div>
        <div class="analysis-card__badges">${badge(p.status.label, p.status.tone)}<span class="score-chip">${item.score}%</span></div>
      </div>
      <div class="progress-track"><div class="progress-bar progress-bar--${p.status.tone}" style="width:${Math.max(item.score, 3)}%"></div></div>
      <div class="analysis-card__meta"><span>Confidence: <strong>${p.confidence}</strong></span><span>${escapeHtml(p.summary)}</span></div>
      <div class="explanation-box"><span class="explanation-box__label">AI Explanation</span><p class="explanation-box__text">${escapeHtml(p.explanation)}</p></div>
      ${buildExpectedFoundBox(p.expectedFound, p.reason)}
      ${buildComparisonTable(p.comparisons)}
    </div>`;
};

export const buildReportHTML = ({
  analysisData,
  listId,
  logoUrl = imgUrbanLight,
  applicationId,
  reportId,
  generatedAt,
  companyName = 'Urban Compass AI',
}) => {
  const total = analysisData.length;
  const matched = analysisData.filter((i) => i.score >= 90).length;
  const partial = analysisData.filter((i) => i.score >= 40 && i.score < 90).length;
  const failed = analysisData.filter((i) => i.score < 40).length;
  const avg = total ? Math.round(analysisData.reduce((s, i) => s + (i.score || 0), 0) / total) : 0;

  const now = generatedAt instanceof Date ? generatedAt : new Date();
  const appId = applicationId || listId || '—';
  const rId =
    reportId ||
    `RPT-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 9000) + 1000)}`;

  const recommendation = recommendationFor(avg);
  const hStatus = headerStatus(avg);

  return `
  <div class="report-root">
    <style>${REPORT_CSS}</style>
    <div class="report-header" data-report-block>
      <div class="report-header__left">
        ${logoUrl ? `<img src="${logoUrl}" class="report-logo" />` : `<div class="report-logo-fallback">${escapeHtml(companyName)}</div>`}
        <div>
          <h1 class="report-title">Document Verification Report</h1>
          <p class="report-subtitle">${escapeHtml(companyName)} · Automated Verification System</p>
        </div>
      </div>
      <div class="report-header__right">
        ${badge(hStatus.label, hStatus.tone)}
        <div class="report-meta">
          <div><span>Generated</span> <strong>${formatDateTime(now)}</strong></div>
          <div><span>Application ID</span> <strong>${escapeHtml(String(appId))}</strong></div>
          <div><span>Report ID</span> <strong>${escapeHtml(rId)}</strong></div>
        </div>
      </div>
    </div>

    <div class="section-title" data-report-block>Executive Summary</div>
    ${buildSummaryCards({ total, matched, partial, failed, avg })}

    <div class="section-title" data-report-block>Detailed Analysis</div>
    ${analysisData.map((item, idx) => buildAnalysisCard(item, idx)).join('')}

    <div class="section-title" data-report-block>Recommendation</div>
    <div class="recommendation-card recommendation-card--${recommendation.tone}" data-report-block>
      <div class="recommendation-card__title">${escapeHtml(recommendation.title)}</div>
      <div class="recommendation-card__body">${escapeHtml(recommendation.body)}</div>
    </div>

    <div class="report-footer" data-report-block>
      <div>Generated by ${escapeHtml(companyName)}</div>
      <div class="report-footer__disclaimer">This report was produced using AI-assisted document comparison and should be used as a decision-support aid, not as a sole basis for approval.</div>
    </div>
  </div>`;
};

const REPORT_CSS = `
  .report-root { font-family:'Helvetica Neue',Arial,sans-serif; color:${COLORS.textPrimary}; background:#fff; padding:36px; box-sizing:border-box; }
  .report-root * { box-sizing:border-box; }
  .badge { display:inline-block; padding:4px 10px; border-radius:999px; font-size:12px; font-weight:600; letter-spacing:.02em; text-transform:uppercase; }
  .badge--success { background:${COLORS.successBg}; color:${COLORS.success}; border:1px solid ${COLORS.success}33; }
  .badge--warning { background:${COLORS.warningBg}; color:${COLORS.warning}; border:1px solid ${COLORS.warning}33; }
  .badge--danger { background:${COLORS.dangerBg}; color:${COLORS.danger}; border:1px solid ${COLORS.danger}33; }
  .report-header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid ${COLORS.primary}; padding-bottom:20px; margin-bottom:28px; }
  .report-header__left { display:flex; align-items:center; gap:14px; }
  .report-logo { width:60px; height:48px; object-fit:contain; }
  .report-logo-fallback { width:48px; height:48px; border-radius:8px; background:${COLORS.primary}; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:12px; }
  .report-title { font-size:22px; font-weight:700; margin:0; }
  .report-subtitle { font-size:12px; color:${COLORS.textSecondary}; margin:4px 0 0; }
  .report-header__right { text-align:right; }
  .report-meta { margin-top:10px; font-size:12px; color:${COLORS.textSecondary}; display:flex; flex-direction:column; gap:3px; }
  .report-meta span { margin-right:6px; color:${COLORS.textMuted}; }
  .section-title { font-size:14px; font-weight:700; color:${COLORS.primary}; text-transform:uppercase; letter-spacing:.04em; border-left:4px solid ${COLORS.primary}; padding-left:10px; margin:26px 0 14px; }
  .summary-grid { display:flex; gap:20px; margin-bottom:8px; }
  .score-panel { flex:0 0 160px; border:1px solid ${COLORS.border}; border-radius:12px; padding:18px; text-align:center; display:flex; flex-direction:column; align-items:center; gap:8px; }
  .score-ring { width:88px; height:88px; border-radius:50%; display:flex; align-items:center; justify-content:center; border:8px solid ${COLORS.primaryLight}; }
  .score-ring--success { border-color:${COLORS.successBg}; }
  .score-ring--warning { border-color:${COLORS.warningBg}; }
  .score-ring--danger { border-color:${COLORS.dangerBg}; }
  .score-ring__value { font-size:20px; font-weight:700; }
  .score-panel__caption { font-size:12px; color:${COLORS.textMuted}; }
  .summary-cards { flex:1; display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
  .summary-card { border:1px solid ${COLORS.border}; border-radius:10px; padding:14px; display:flex; flex-direction:column; gap:4px; }
  .summary-card__value { font-size:22px; font-weight:700; }
  .summary-card__label { font-size:12px; color:${COLORS.textMuted}; text-transform:uppercase; letter-spacing:.03em; }
  .summary-card--success .summary-card__value { color:${COLORS.success}; }
  .summary-card--warning .summary-card__value { color:${COLORS.warning}; }
  .summary-card--danger .summary-card__value { color:${COLORS.danger}; }
  .analysis-card { border:1px solid ${COLORS.border}; border-radius:12px; padding:18px 20px; margin-bottom:16px; box-shadow:0 1px 2px rgba(16,24,40,.04); }
  .analysis-card__header { display:flex; justify-content:space-between; align-items:center; margin-bottom:10px; }
  .analysis-card__title-row { display:flex; align-items:center; gap:10px; }
  .analysis-card__index { font-size:12px; color:${COLORS.textMuted}; font-weight:700; }
  .analysis-card__title { font-size:15px; font-weight:700; }
  .analysis-card__badges { display:flex; align-items:center; gap:8px; }
  .score-chip { font-size:12px; font-weight:700; color:${COLORS.primary}; background:${COLORS.primaryLight}; padding:3px 9px; border-radius:6px; }
  .progress-track { width:100%; height:7px; background:#f1f5f9; border-radius:999px; overflow:hidden; margin-bottom:10px; }
  .progress-bar { height:100%; border-radius:999px; }
  .progress-bar--success { background:${COLORS.success}; }
  .progress-bar--warning { background:${COLORS.warning}; }
  .progress-bar--danger { background:${COLORS.danger}; }
  .analysis-card__meta { display:flex; justify-content:space-between; font-size:12px; color:${COLORS.textSecondary}; margin-bottom:12px; }
  .explanation-box { background:#f9fafb; border:1px solid ${COLORS.border}; border-radius:8px; padding:12px 14px; margin-bottom:12px; }
  .explanation-box__label { font-size:12px; font-weight:700; color:${COLORS.textMuted}; text-transform:uppercase; letter-spacing:.04em; }
  .explanation-box__text { font-size:12px; color:${COLORS.textSecondary}; line-height:1.6; margin:6px 0 0; }
  .diff-box { display:flex; gap:12px; border:1px solid ${COLORS.dangerBg}; background:${COLORS.dangerBg}; border-radius:8px; padding:12px 14px; margin-bottom:12px; }
  .diff-box__col { flex:1; display:flex; flex-direction:column; gap:3px; }
  .diff-box__label { font-size:12px; font-weight:700; text-transform:uppercase; color:${COLORS.textMuted}; }
  .diff-box__value { font-size:12px; font-weight:600; font-family:'Courier New',monospace; }
  .diff-box__col--found .diff-box__value { color:${COLORS.danger}; }
  .diff-box__col--expected .diff-box__value { color:${COLORS.success}; }
  .compare-table { width:100%; border-collapse:collapse; font-size:12px; }
  .compare-table th { text-align:left; font-size:12px; text-transform:uppercase; color:${COLORS.textMuted}; padding:6px 10px; border-bottom:1px solid ${COLORS.border}; }
  .compare-table td { padding:8px 10px; border-bottom:1px solid ${COLORS.border}; }
  .compare-table td.mono { font-family:'Courier New',monospace; }
  .compare-table tr.row--diff { background:${COLORS.dangerBg}; }
  .recommendation-card { border-radius:12px; padding:16px 20px; margin-bottom:24px; border:1px solid; }
  .recommendation-card--success { background:${COLORS.successBg}; border-color:${COLORS.success}33; }
  .recommendation-card--warning { background:${COLORS.warningBg}; border-color:${COLORS.warning}33; }
  .recommendation-card--danger { background:${COLORS.dangerBg}; border-color:${COLORS.danger}33; }
  .recommendation-card__title { font-weight:700; font-size:13px; margin-bottom:4px; }
  .recommendation-card__body { font-size:12px; color:${COLORS.textSecondary}; line-height:1.5; }
  .report-footer { border-top:1px solid ${COLORS.border}; padding-top:14px; font-size:12px; color:${COLORS.textMuted}; display:flex; flex-direction:column; gap:4px; }
`;

export default buildReportHTML;
